import { IconButton, Menu, MenuItem } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';

type ActionButtonProps = {
  icon: React.ReactNode | string;
  label: string;
  onClick: () => void;
};

function ActionButton({ icon, label, onClick }: ActionButtonProps) {
  return (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{ display: 'flex', alignItems: 'center', gap: '12px', height: '40px' }}
    >
      {typeof icon === 'string' ? <i className={icon} style={{ fontSize: '16px', color: '#667085' }} /> : icon}
      <Typography style={{ color: 'var(--Gray-700, #344054)', fontFamily: 'Inter', fontSize: '14px', fontStyle: 'normal', fontWeight: 500, lineHeight: '20px' }}>
        {label}
      </Typography>
    </MenuItem>
  );
}

type Props = {
  isCompleted: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onStatusChange?: (isCompleted: boolean) => void;
  onMoveToBacklog?: () => void;
};

export function NoteActions({ onDelete, onEdit, onStatusChange, isCompleted, onMoveToBacklog }: Props) {
  const anchorElRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        ref={anchorElRef}
        sx={{ width: '22px', height: '22px' }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <i className="fa-regular fa-ellipsis-vertical" style={{ fontSize: '16px', color: '#667085' }} />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={open}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={(e: any) => {
          e.stopPropagation();
          setOpen(false);
        }}
        sx={{
          '.MuiPaper-root': {
            background: 'white',
            borderRadius: '8px',
            border: '1px solid var(--Gray-100, #F2F4F7)',
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
          },
        }}
        MenuListProps={{
          sx: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }}
      >
        {isCompleted ? (
          <ActionButton
            label="Mark as incomplete"
            icon={<i className="fa-solid fa-arrow-rotate-left" style={{ color: '#FB6514', fontSize: '16px' }} />}
            onClick={() => {
              onStatusChange?.(false);
              setOpen(false);
            }}
          />
        ) : (
          <ActionButton
            label="Mark as complete"
            icon={<i className="fa fa-check" style={{ color: '#12B76A', fontSize: '16px' }} />}
            onClick={() => {
              onStatusChange?.(true);
              setOpen(false);
            }}
          />
        )}

        {onEdit ? (
          <ActionButton
            label="Edit"
            icon="fa-regular fa-pen-to-square"
            onClick={() => {
              onEdit?.();
              setOpen(false);
            }}
          />
        ) : null}

        {onDelete ? (
          <ActionButton
            label="Delete"
            icon="fa-regular fa-trash-can"
            onClick={() => {
              onDelete?.();
              setOpen(false);
            }}
          />
        ) : null}

        {onMoveToBacklog ? (
          <ActionButton
            label="Back to backlog"
            icon="fa-regular fa-arrow-right-from-bracket"
            onClick={() => {
              onMoveToBacklog();
              setOpen(false);
            }}
          />
        ) : null}
      </Menu>
    </>
  );
}
